import { useState } from "react";
import FixaTable, {
  FixaTableBody,
  FixaTableHeader,
} from "@coworker/apprestructured/src/shared/wrappers/FixaTable/FixaTable";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import TrashCanIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/TrashCanIconPath";
import { FixaSwitch } from "@coworker/apprestructured/src/shared/wrappers/FixaSwitch/FixaSwitch";
import { UserPermission } from "../../types/UserPermission";

type SortColumn = "id" | "uid" | "email" | "permission_id" | "value";
type SortDirection = "asc" | "desc";

type Props = {
  permissions: UserPermission[];
  isLoading: boolean;
  onToggle: (id: number, currentValue: boolean) => void;
  onDelete: (id: number) => void;
};

const UserPermissionsPanel = ({
  permissions,
  isLoading,
  onToggle,
  onDelete,
}: Props) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>("email");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleHeaderClick = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  const sortedPermissions = [...permissions].sort(
    (a: UserPermission, b: UserPermission) => {
      const aVal = a[sortColumn] ?? "";
      const bVal = b[sortColumn] ?? "";

      const aComp = typeof aVal === "boolean" ? (aVal ? 1 : 0) : aVal;
      const bComp = typeof bVal === "boolean" ? (bVal ? 1 : 0) : bVal;

      if (aComp < bComp) return sortDirection === "asc" ? -1 : 1;
      if (aComp > bComp) return sortDirection === "asc" ? 1 : -1;
      return 0;
    }
  );

  return (
    <FixaTable fullWidth>
      <FixaTableHeader>
        <tr>
          <th
            style={{ cursor: "pointer" }}
            onClick={() => handleHeaderClick("id")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && handleHeaderClick("id")
            }
            aria-sort={
              sortColumn === "id"
                ? sortDirection === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
          >
            ID{sortIndicator("id")}
          </th>
          <th
            style={{ cursor: "pointer" }}
            onClick={() => handleHeaderClick("uid")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && handleHeaderClick("uid")
            }
            aria-sort={
              sortColumn === "uid"
                ? sortDirection === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
          >
            UID{sortIndicator("uid")}
          </th>
          <th
            style={{ cursor: "pointer" }}
            onClick={() => handleHeaderClick("email")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && handleHeaderClick("email")
            }
            aria-sort={
              sortColumn === "email"
                ? sortDirection === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
          >
            Email{sortIndicator("email")}
          </th>
          <th
            style={{ cursor: "pointer" }}
            onClick={() => handleHeaderClick("permission_id")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") &&
              handleHeaderClick("permission_id")
            }
            aria-sort={
              sortColumn === "permission_id"
                ? sortDirection === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
          >
            Permission Code{sortIndicator("permission_id")}
          </th>
          <th
            style={{ cursor: "pointer" }}
            onClick={() => handleHeaderClick("value")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && handleHeaderClick("value")
            }
            aria-sort={
              sortColumn === "value"
                ? sortDirection === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
          >
            Value{sortIndicator("value")}
          </th>
          <th>Delete</th>
        </tr>
      </FixaTableHeader>
      <FixaTableBody>
        {isLoading ? (
          <tr>
            <td colSpan={6}>Loading...</td>
          </tr>
        ) : permissions.length === 0 ? (
          <tr>
            <td colSpan={6}>No user permissions found</td>
          </tr>
        ) : (
          sortedPermissions.map((permission: UserPermission) => (
            <tr key={permission.id}>
              <td>{permission.id}</td>
              <td>{permission.uid}</td>
              <td>{permission.email ?? "—"}</td>
              <td>{permission.permission_id}</td>
              <td>
                <FixaSwitch
                  checked={permission.value}
                  id={`user-permission-${permission.id}`}
                  value={permission.value.toString()}
                  onChange={() => onToggle(permission.id, permission.value)}
                />
              </td>
              <td>
                <FixaButton
                  iconOnly={true}
                  size="small"
                  ssrIcon={TrashCanIconPath}
                  onClick={() => onDelete(permission.id)}
                />
              </td>
            </tr>
          ))
        )}
      </FixaTableBody>
    </FixaTable>
  );
};

export default UserPermissionsPanel;
